<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>
    <div>
      <NuxtLayout name="center">
        <h1 class="mt-6 sm:mt-0 text-center">{{ generalReviews.title }}</h1>
        <div class="max-w-6xl mx-auto">
          <p class="mx-4 mt-10">
            {{ generalReviews.intro_text }}
          </p>
        </div>
      </NuxtLayout>

      <section class="bg-[#F0FBFB] my-15 border-b-[8px] border-b-primary">
        <NuxtLayout name="center">
          <h2 class="sr-only">General rating</h2>
          <div class="max-w-[59rem] mx-auto relative sm:pt-[2rem]">
            <div
              class="relative w-fit mx-auto md:mr-0 p-4 md:pr-12 md:mb-16 flex flex-row flex-wrap gap-2 items-center"
            >
              <div>
                <span class="font-semibold text-[6em]">{{ generalReviews.average_review }}</span>
              </div>
              <div class="flex flex-col gap-[.1em]">
                <span class="font-bold text-[1.4em] mt-0">
                  {{ getRatingScoreLabel(generalReviews.average_review) }}
                </span>
                <span class="text-[19px]">
                  <CommonStarsRating :value="generalReviews.average_review" />
                </span>
                <span>{{ generalReviews.total_reviews }} {{ $t("reviewBlock.reviews") }}</span>
              </div>
            </div>

            <NuxtImg
              loading="lazy"
              src="/img/reviews_landing-wallpaper.svg"
              alt=""
              class="max-h-[332px] bottom-0 translate-y-14 -mt-12 md:-mt-16 md:translate-y-1 md:absolute"
              draggable="false"
              width="619"
              height="332"
            />

            <div
              class="bg-white rounded relative sm:max-w-[407px] shadow-[0px_3px_6px_#00000029] ml-auto p-5 translate-y-14 flex flex-col-reverse gap-3"
            >
              <ReviewsProgressbar
                v-for="n in Object.keys(generalReviews.ratings).length"
                :key="n"
                :label="$t('reviewBlock.star', { count: n })"
                :progress="Number(generalReviews.ratings[`${n}-star`])"
              />
            </div>
          </div>
        </NuxtLayout>
      </section>

      <section class="lg:px-0 mt-[5.5rem] sm:mt-[8rem]">
        <NuxtLayout name="center">
          <div class="md:mx-auto md:max-w-[48rem]">
            <h2 class="sr-only">Customers reviews</h2>

            <div class="flex flex-col gap-4 sm:gap-10">
              <ReviewsCustomerComment
                v-for="n in reviewsList.length"
                :stars="Number(reviewsList[n - 1].rating)"
                :customer-name="reviewsList[n - 1].customer_name"
                :customer-location="reviewsList[n - 1].location"
                :comment="reviewsList[n - 1].review"
              />

              <BaseButton
                :text="$t('common.btnLoadReviews')"
                variant="outlined"
                class="w-fit mx-auto mt-4 sm:mt-0"
                :loading="!!loadingReviews"
                @click="handleLoadMoreReviews"
                v-if="canLoadMoreReviewsRef"
              />
            </div>
          </div>
        </NuxtLayout>
      </section>

      <section class="mt-16 border-b-[1rem] border-b-primary">
        <h2 class="sr-only">Need help?</h2>
        <ClinicCTABlock imgSrc="USPs_graphic_03_1" :getStarted="true"></ClinicCTABlock>
        <!-- <div class="mx-auto flex flex-row flex-wrap relative">
          <div
            class="flex-[50%] flex-shrink-0 flex flex-row items-end px-4 -mb-[4px] sm:mb-[1rem]"
          >
            <NuxtImg
              src="/img/reviews-help-section.svg"
              alt=""
              class="sm:mx-auto max-h-[80%] max-w-[16rem]"
            />
          </div>
          <span
            class="h-[1rem] w-full bg-[#00A1AA99] absolute bottom-0 left-0"
          ></span>
          <div
            class="custom-bg md:reviews-custom-clip-path bg-primary clip text-white flex-[50%] md:px-32 py-10 flex flex-row items-center box-border relative sm:rounded-tl-lg"
          >
            <div class="max-w-[25rem] mx-auto">
              <div
                class="text-[1.5em] md:text-[2em] font-semibold leading-tight text-center md:text-justify"
              >
              <p  v-html="$t('clinic.ctaBlock.title1')"></p>
              </div>
              <BaseButton
                :text="$t('common.btnGetStarted')"
                class="px-6 py-[1.25em] font-semibold text-[1em] min-w-[18em] mt-6 mx-auto"
              />
            </div>
          </div>
        </div> -->
      </section>
    </div>
  </div>
</template>

<script setup>
const currentPage = ref(1);
const loadingReviews = ref(false);

const generalReviews = ref([]);
generalReviews.value = await useGeneralReviews();

const canLoadMoreReviewsRef = ref(false);
const reviewsList = ref([]);
const reviewsListResponse = await loadReviewsList();
reviewsList.value = reviewsListResponse.list;
canLoadMoreReviewsRef.value = reviewsListResponse.canLoadMore;

// Loading more reviews to the page
const handleLoadMoreReviews = async () => {
  currentPage.value += 1;

  const { list: newData, canLoadMore } = await loadReviewsList();
  const enhancedList = [...toRaw(reviewsList.value), ...toRaw(newData)];

  reviewsList.value = enhancedList;
  canLoadMoreReviewsRef.value = canLoadMore;
};

async function loadReviewsList() {
  loadingReviews.value = true;
  const res = await useReviewsList(currentPage.value);

  loadingReviews.value = false;
  return res;
}

const breadcrumbs = getBreadcrumbs("reviews");

useMetaTitles(generalReviews.value, "reviews");
</script>

<style scoped>
.custom-bg {
  transform: translateY(1px);
}
</style>
